<template>
  <full-screen :title="t('pay-in-pay-out.title')" @close="router.back()">
    <div class="flex flex-col justify-center items-center h-full pt-4 px-4">
      <div class="flex flex-col gap-4 max-w-[33rem] w-full h-full min-h-0">
        <l-option-selector v-model="selectedOption" :options="options" class="shrink-0" />
        <div class="flex-1 flex flex-col min-h-0 max-h-[20.75rem]">
          <calculator
            :initial-to-pay="total"
            keypad-mode="simple"
            class="h-full"
            display-mode="toPay"
            @change="onChange"
          />
        </div>
        <div class="shrink-0">
          <l-field :label="t('pay-in-pay-out.detail')">
            <l-textarea v-model="detail" class="h-20" />
          </l-field>
        </div>
      </div>
    </div>
    <template #footer>
      <div class="w-[33rem] flex justify-center">
        <l-button class="max-w-64 w-full" :disabled="saving" @click="saveMovement()">
          {{
            selectedOption === 'payIn' ? t('pay-in-pay-out.pay-in') : t('pay-in-pay-out.pay-out')
          }}
          <span>
            {{ currency(total) }}
          </span>
        </l-button>
      </div>
    </template>
  </full-screen>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'

import { LButton, LField, LOptionSelector, LTextarea } from '@last/core-ui/paprika'

import Calculator from '@/components/Calculator/Calculator.vue'
import type { CalculatorPayload } from '@/components/Calculator/types'
import FullScreen from '@/components/FullScreen.vue'
import { useMoney } from '@/composables/useMoney'
import CashMachine from '@/integrations/cashmachine/cashmachine'
import TicketPrinter from '@/integrations/printer/ticketPrinter'
import { useToast } from '@/plugins/toast/toast'
import { useAuthStore } from '@/store/auth'
import { useTillStore } from '@/store/till'

const authStore = useAuthStore()
const tillStore = useTillStore()
const { t } = useI18n()
const { currency } = useMoney()
const router = useRouter()

const { currentEmployee } = storeToRefs(authStore)
const { addMovement } = tillStore
const { selectedCashTillIsStarted } = storeToRefs(tillStore)
const toast = useToast()

const options = [
  {
    label: t('pay-in-pay-out.pay-in'),
    value: 'payIn'
  },
  {
    label: t('pay-in-pay-out.pay-out'),
    value: 'payOut'
  }
]

const selectedOption = ref(options[0].value)
const total = ref(0)
const detail = ref()
const saving = ref(false)

const hasCashMachine = computed(() => {
  return CashMachine.isEnabled()
})

const machineAmount = computed(() => {
  return CashMachine.inputAmount.value
})

watch(machineAmount, amount => {
  total.value = Math.abs(amount)
})

onMounted(() => {
  if (!selectedCashTillIsStarted.value) {
    router.push({ name: 'employees' })
  }
  if (hasCashMachine.value) {
    total.value = machineAmount.value
  }
})

function onChange(event: CalculatorPayload) {
  total.value = event.toPay
}

async function saveMovement() {
  if (saving.value) return
  saving.value = true
  try {
    let method = 'cash'
    if (hasCashMachine.value) {
      await cashMachineAction()
      method = CashMachine.method!
    }
    if (total.value === 0) return
    const movement = {
      amount: total.value,
      detail: detail.value,
      movementType: selectedOption.value,
      method,
      tillId: currentEmployee.value?.tillEnabled ? (currentEmployee.value.tillId ?? null) : null
    }
    addMovement(movement)
    TicketPrinter.printPayInPayOut({
      date: new Date(),
      employee: currentEmployee.value?.name,
      ...movement
    })
    toast({
      title: t('pay-in-pay-out.movement-added')
    })
    total.value = 0
    detail.value = ''
  } catch (error) {
    toast({
      title: (error as Error).message
    })
  } finally {
    saving.value = false
  }
}

async function cashMachineAction() {
  if (selectedOption.value === 'payIn') {
    total.value = Math.abs(await CashMachine.payIn(total.value))
  } else {
    // Pay out from the machine is negative, and the server needs a positive number
    total.value = Math.abs(await CashMachine.payOut(total.value))
  }
}
</script>
